import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Save, Sparkles, UserPlus } from 'lucide-react';
import { useCurrentUser } from '../../hooks/useAuth';
import { useCurrentScope } from '../../hooks/useMobilisations';
import { Button } from '../../components/ui/Button';
import { PersonDetailsForm } from '../../components/person/PersonDetailsForm';
import {
  buildCreatePersonBody,
  emptyPersonDetails,
  personDetailsValid,
  type PersonDetailsValue,
} from '../../components/person/personDetails';
import type { AllocationTarget } from '../../components/admin/AllocationTargetField';
import { canRunWizard } from '../onboarding/NewEmployee/canRunWizard';
import { api, ApiError } from '../../lib/api';

interface PersonResponse {
  person: {
    id: string;
    full_name: string;
    email: string;
  };
}

/**
 * Manual "+ Add person" form. The quick single-record path: sister to the CSV
 * import for bulk and to the guided new-employee wizard for a starter who
 * needs training / documents / forms setting up at the same time. Hits POST
 * /api/admin/people; on success redirects to the new person's edit page.
 */
export function AttendeeNew() {
  const { data: user, isLoading } = useCurrentUser();
  const scope = useCurrentScope();
  const navigate = useNavigate();
  const [details, setDetails] = useState<PersonDetailsValue>(emptyPersonDetails);
  const [target, setTarget] = useState<AllocationTarget>(undefined);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (isLoading)
    return <main className="p-6 text-sm text-fg-muted">Loading...</main>;
  if (!user) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        Please <Link to="/login" className="underline">sign in</Link>.
      </main>
    );
  }
  // Keep in step with createGate on POST /api/admin/people, or a role the
  // API accepts is turned away by the page it needs.
  const allowed = user.roles.some((r) =>
    ['admin', 'super_admin', 'coordinator', 'hr'].includes(r),
  );
  if (!allowed) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        You do not have permission to add people.
      </main>
    );
  }

  // Day-to-day onboarding has no "attendees"; it has new employees.
  const dayToDay = scope?.kind === 'onboarding';
  const noun = dayToDay ? 'employee' : 'attendee';

  // Scoped sessions inherit the header switcher's mobilisation; the
  // all-mobilisations view must pick a destination explicitly.
  const scoped = !!user.current_mobilisation_id;
  const targetChosen = scoped || target !== undefined;

  async function onSave() {
    setSaving(true);
    setError(null);
    try {
      const res = await api<PersonResponse>('/api/admin/people', {
        method: 'POST',
        body: JSON.stringify(buildCreatePersonBody(details, { scoped, target })),
      });
      toast.success(`Added ${res.person.full_name}`);
      navigate(`/admin/people/${res.person.id}`);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'save failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <main className="min-h-screen bg-bg text-fg">
      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6 sm:py-8">
        <Link
          to="/admin/people"
          className="inline-flex items-center gap-1 text-xs font-medium text-fg-muted hover:text-fg"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to people
        </Link>

        <header className="mt-4 flex items-start gap-4">
          <div className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-accent/10 text-accent">
            <UserPlus className="h-6 w-6" strokeWidth={2} />
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-accent">
              People
            </div>
            <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
              Add {noun}
            </h1>
            <p className="mt-1 text-sm text-fg-muted">
              For one-off adds. Bulk loads still go through{' '}
              <Link
                to="/admin/import-attendees"
                className="font-medium underline-offset-2 hover:underline"
              >
                import attendees
              </Link>
              .
            </p>
          </div>
        </header>

        {/* Only offered to roles that can actually finish it; a coordinator
            cannot assign onboarding content and would stall at step 2. */}
        {canRunWizard(user.roles) ? (
          <Link
            to="/onboarding/new"
            className="mt-5 flex items-start gap-3 rounded-token border border-accent/20 bg-accent/10 px-4 py-3 text-sm text-fg transition-colors hover:bg-accent/15"
          >
            <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
            <span>
              <span className="font-semibold">Setting up a new starter?</span> The
              guided new employee flow adds them and walks through their training,
              documents to sign, forms and tasks in one go.
            </span>
          </Link>
        ) : null}

        <div className="mt-4 rounded-xl border border-line/10 bg-surface p-5 shadow-sm">
          <PersonDetailsForm
            value={details}
            onChange={setDetails}
            target={target}
            onTargetChange={setTarget}
            autoFocus
          />

          {error ? (
            <div className="mt-4 rounded-md bg-danger-soft/15 px-3 py-2 text-sm text-danger-soft-fg">
              {error}
            </div>
          ) : null}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button
            variant="primary"
            size="md"
            iconLeft={<Save className="h-4 w-4" />}
            loading={saving}
            disabled={!personDetailsValid(details) || !targetChosen}
            onClick={onSave}
          >
            Add {noun}
          </Button>
          <Link to="/admin/people">
            <Button variant="ghost" size="md">
              Cancel
            </Button>
          </Link>
        </div>
      </div>
    </main>
  );
}
